import { CommonFilterModel, CommonParams, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { StatisticalAnalysisGeneralStatisticsDossierBaseRequest, StatisticalAnalysisGeneralStatisticsDossierCase, StatisticalAnalysisGeneralStatisticsDossierDetailExternalViewTable, StatisticalAnalysisGeneralStatisticsDossierDetailRequest, StatisticalAnalysisGeneralStatisticsDossierDetailViewChart, StatisticalAnalysisGeneralStatisticsDossierDetailViewChartResponseInternalGroups, StatisticalAnalysisGeneralStatisticsDossierDetailViewTable, StatisticalAnalysisGeneralStatisticsDossierGroupRequest, StatisticalAnalysisGeneralStatisticsDossierTrackingElements } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.dto'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import { StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { StatisticalAnalysisTemporalFilterEnum } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { StatisticalAnalysisViewDetailChartData } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart/statistical-analysis-view-detail-chart.model'
import { formatDatetimes, StatiscalAnalysisTypeCases, StatisticalAnalysisCaseTypes, StatisticalAnalysisDynamicObject } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisGeneralDossierExternalInfo } from '@/modules/statistical-analysis-module/views/general-statistics-dossier/general-statistics-dossier.model'
import { Nullable } from '@/shared/types/app.types'
import axios, { AxiosResponse } from 'axios'
import { SeriesOptionsType } from 'highcharts'
import { chain, find, isNil, map } from 'lodash'
import { store } from '@/store/store'

class GeneralStatisticsDossierService {
    async getGeneralView(params: StatisticalAnalysisGeneralStatisticsDossierBaseRequest, entityType: StatisticalAnalysisSidebarItemCombo): Promise<StatisticalAnalysisGeneralStatisticsDossierCase> {
        const response = await axios.get<StatisticalAnalysisGeneralStatisticsDossierCase>(`/statisticAnalysis/dossiers/general/${entityType?.id?.toLowerCase()}`, { params })
        return response.data
    }

    async getGeneralViewTracking(params: StatisticalAnalysisGeneralStatisticsDossierBaseRequest, generalState: StatisticalAnalysisCaseTypes, entityType: StatisticalAnalysisSidebarItemCombo): Promise<StatisticalAnalysisGeneralStatisticsDossierTrackingElements[]> {
        const response = await axios.get<StatisticalAnalysisGeneralStatisticsDossierTrackingElements[]>(`/statisticAnalysis/dossiers/general/${entityType?.id?.toLowerCase()}/${generalState}`, { params })
        return response.data
    }

    async getDetailViewTable(
        filterModel: CommonFilterModel, commonParams: CommonParams,
        typeCase: StatiscalAnalysisTypeCases, state: StatisticalAnalysisCaseTypes,
        detailState: StatisticalAnalysisCaseTypes, baseRequest: StatisticalAnalysisGeneralStatisticsDossierBaseRequest,
        detailRequest: StatisticalAnalysisGeneralStatisticsDossierDetailRequest, scrollId: Nullable<string>
    ): Promise<StatisticalAnalysisGeneralStatisticsDossierDetailViewTable> {
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest,
            ...detailRequest
        }
        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }

        const response = await axios.get<StatisticalAnalysisGeneralStatisticsDossierDetailViewTable>(`/statisticAnalysis/dossiers/general/${typeCase?.toLowerCase()}/${state}/${detailState}`, { params })
        return response.data
    }

    async getExternalViewGeneralList(filterModel: CommonFilterModel, commonParams: CommonParams, typeCase: StatiscalAnalysisTypeCases, state: StatisticalAnalysisCaseTypes, baseRequest: StatisticalAnalysisGeneralStatisticsDossierBaseRequest, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisGeneralStatisticsDossierDetailExternalViewTable> {
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }
        const response = await axios.get<StatisticalAnalysisGeneralStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/general/${typeCase?.toLowerCase()}/${state}/generalList`, { params })
        return response.data
    }

    async getExternalViewDetailList(filterModel: CommonFilterModel, commonParams: CommonParams, state: StatisticalAnalysisCaseTypes, subState: StatisticalAnalysisCaseTypes, typeCase: StatiscalAnalysisTypeCases, baseRequest: StatisticalAnalysisGeneralStatisticsDossierBaseRequest, groupingParams: StatisticalAnalysisDynamicObject, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisGeneralStatisticsDossierDetailExternalViewTable> {
        const bodyParams = {
            ...groupingParams
        }

        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }

        const response = await axios.put<StatisticalAnalysisGeneralStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/general/${typeCase?.toLowerCase()}/${state}/${subState}/detailList`, bodyParams, { params })
        return response.data
    }

    async getDetailViewChart(typeCase: StatiscalAnalysisTypeCases, state: StatisticalAnalysisCaseTypes, subState: StatisticalAnalysisCaseTypes, baseRequest: StatisticalAnalysisGeneralStatisticsDossierBaseRequest, groupRequest: StatisticalAnalysisGeneralStatisticsDossierGroupRequest): Promise<StatisticalAnalysisViewDetailChartData> {
        const params = {
            ...baseRequest,
            ...groupRequest
        }
        const response = await axios.get<StatisticalAnalysisGeneralStatisticsDossierDetailViewChart[]>(`/statisticAnalysis/dossiers/general/${typeCase?.toLowerCase()}/${state}/${subState}/stats`, { params })
        return this._parseChartData(response.data)
    }

    public getBaseRequest(filter: StatisticalAnalysisGeneralDossierExternalInfo): StatisticalAnalysisGeneralStatisticsDossierBaseRequest {
        return {
            startDate: encodeURIComponent(formatDatetimes(filter.startDate as Date)),
            endDate: encodeURIComponent(formatDatetimes(filter.endDate as Date)),
            sameRange: filter.sameRange,
            laboratoryIds: (filter.laboratories as LaboratoryStatisticalAnalysisSidebarDateInput[]).map((item: LaboratoryStatisticalAnalysisSidebarDateInput) => item.labId),
            laboratoryUserId: store.getters.labId,
            typeIds: filter.typeIds!.map((typeId: StatisticalAnalysisSidebarItemCombo) => parseInt(typeId.id) as number)
        }
    }

    public generalExportTo(
        externalFilter: StatisticalAnalysisGeneralDossierExternalInfo,
        bodyParams: ReportGridRequest,
        exportType: CommonExportType): Promise<AxiosResponse> {
        const params = {
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }
        return axios.put(`statisticAnalysis/dossiers/general/${externalFilter.typeSearch!.id.toLocaleLowerCase()}/${externalFilter.trackingGeneralSelected?.key}/generalList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => {
            return response
        })
    }

    public detailExportTo(externalFilter: StatisticalAnalysisGeneralDossierExternalInfo, reportGridRequest: ReportGridRequest, groupValueFilterRequests: StatisticalAnalysisDynamicObject[], infoDetail: StatisticalAnalysisTemporalFilterEnum, groupByFields: string[], exportType: CommonExportType): Promise<AxiosResponse> {
        const params = {
            ...{ infoDetail },
            ...{ groupByFields },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }
        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequests: groupValueFilterRequests
        }

        return axios.put(`statisticAnalysis/dossiers/general/${externalFilter.typeSearch!.id.toLocaleLowerCase()}/${externalFilter.trackingGeneralSelected?.key}/${externalFilter.trackingDetailSelected?.key}/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    public detailExternalExportTo(externalFilter: StatisticalAnalysisGeneralDossierExternalInfo, reportGridRequest: ReportGridRequest, groupValueFilterRequest: StatisticalAnalysisDynamicObject, infoDetail: StatisticalAnalysisTemporalFilterEnum, exportType: CommonExportType): Promise<AxiosResponse> {
        const params = {
            ...{ infoDetail },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }
        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequest
        }

        return axios.put(`statisticAnalysis/dossiers/general/${externalFilter.typeSearch!.id.toLocaleLowerCase()}/${externalFilter.trackingGeneralSelected?.key}/${externalFilter.trackingDetailSelected?.key}/detailList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    private _parseChartData(response: StatisticalAnalysisGeneralStatisticsDossierDetailViewChart[]): StatisticalAnalysisViewDetailChartData {
        const categories = map(response, 'groupBy1')
        const labelSeries = chain(response).map('groupBy2').flatMap().map('name').uniq().value()
        const parsedResponse = this._getDataSeriesChart(labelSeries, response)
        const series = chain(parsedResponse).map('groupBy2').flatMap().groupBy('name').mapValues((value: any, key: string) => {
            return {
                name: key,
                data: map(value, 'count')
            }
        }).flatMap().value() as SeriesOptionsType[]
        return {
            categories,
            series
        }
    }

    private _getDataSeriesChart(uniqLabels: string[], response:StatisticalAnalysisGeneralStatisticsDossierDetailViewChart[]): StatisticalAnalysisGeneralStatisticsDossierDetailViewChart[] {
        return response.map((groupResponse: StatisticalAnalysisGeneralStatisticsDossierDetailViewChart) => {
            groupResponse.groupBy2 = uniqLabels.map((uniqLabel: string) => {
                const foundGroupBy2Item = find(groupResponse.groupBy2, { 'name': uniqLabel })
                if (foundGroupBy2Item) {
                    return foundGroupBy2Item
                } else {
                    return {
                        name: uniqLabel,
                        count: null
                    }
                }
            }) as StatisticalAnalysisGeneralStatisticsDossierDetailViewChartResponseInternalGroups[]
            return groupResponse
        })
    }
}

export const generalStatisticsDossierService = new GeneralStatisticsDossierService()
